import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { endpoints } from "@/api";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Droplet, Plus, Trash2, Pencil, X, Copy, Search } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "./ConfirmDialog";
import { CardSkeleton } from "./Skeletons";

const BATCH_FIELDS = [
  ["batch_name",   { en: "Batch name",   ar: "اسم الدفعة" },     { en: "HOT100 / COLD260", ar: "HOT100 / COLD260" }],
  ["grind_size",   { en: "Grind size",   ar: "الطحن" },          { en: "8.5",              ar: "8.5" }],
  ["gram_of_bean", { en: "Gram of bean", ar: "جرام البن" },      { en: "60g",              ar: "60g" }],
  ["volume",       { en: "Volume",       ar: "الحجم" },          { en: "1000ml",           ar: "1000ml" }],
  ["brew_time",    { en: "Brew time",   ar: "زمن التخمير" },     { en: "4:30",             ar: "4:30" }],
  ["prewet_pct",   { en: "Prewet %",    ar: "Prewet ٪" },        { en: "10%",              ar: "10٪" }],
  ["prewet_time",  { en: "Prewet time", ar: "زمن Prewet" },      { en: "40s",              ar: "40 ث" }],
  ["drip_delay",   { en: "Drip delay",  ar: "تأخير التنقيط" },   { en: "0s",               ar: "0 ث" }],
  ["pulses",       { en: "Pulses",      ar: "عدد النبضات" },     { en: "4",                ar: "4" }],
  ["bypass_pct",   { en: "Bypass %",   ar: "Bypass ٪" },         { en: "10%",              ar: "10٪" }],
  ["ice",          { en: "Ice",        ar: "الثلج" },            { en: "400g",             ar: "400g" }],
];

export default function DripTab() {
  const { isRTL } = useI18n();
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const [editing, setEditing] = useState(null);
  const [query, setQuery] = useState("");

  const { data: recipes = [], isLoading } = useQuery({
    queryKey: ["mycafe-drip"], queryFn: endpoints.myCafeDrip,
  });

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return recipes;
    return recipes.filter((r) =>
      [r.name, r.branch_name, r.notes].filter(Boolean).some((s) => s.toLowerCase().includes(q))
    );
  }, [recipes, query]);

  const startNew = () => setEditing({ id: null, name: "", branch_name: "", batches: [{ batch_name: "HOT100" }], notes: "" });
  const startEdit = (r) => setEditing({ ...r, batches: r.batches?.length ? r.batches : [{ batch_name: "" }] });
  const cancel = () => setEditing(null);

  const setBatch = (idx, patch) =>
    setEditing((prev) => ({ ...prev, batches: prev.batches.map((b, i) => i === idx ? { ...b, ...patch } : b) }));
  const addBatch = () => setEditing((prev) => ({ ...prev, batches: [...prev.batches, { batch_name: "" }] }));
  const removeBatch = (idx) => setEditing((prev) => ({ ...prev, batches: prev.batches.filter((_, i) => i !== idx) }));

  const save = useMutation({
    mutationFn: async () => {
      if (!editing.name?.trim()) throw new Error(isRTL ? "اسم الوصفة مطلوب" : "Recipe name required");
      const payload = { name: editing.name, branch_name: editing.branch_name, batches: editing.batches, notes: editing.notes };
      return editing.id ? endpoints.myCafeUpdateDrip(editing.id, payload) : endpoints.myCafeCreateDrip(payload);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["mycafe-drip"] }); toast.success(isRTL ? "تم الحفظ" : "Saved"); cancel(); },
    onError: (e) => toast.error(e.message || "Error"),
  });

  const del = useMutation({
    mutationFn: (id) => endpoints.myCafeDeleteDrip(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["mycafe-drip"] }); toast.success(isRTL ? "تم الحذف" : "Deleted"); },
  });

  const duplicate = useMutation({
    mutationFn: async (r) => endpoints.myCafeCreateDrip({
      name: `${r.name} (${isRTL ? "نسخة" : "copy"})`,
      branch_name: r.branch_name,
      batches: r.batches,
      notes: r.notes,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["mycafe-drip"] }); toast.success(isRTL ? "تم النسخ" : "Duplicated"); },
  });

  const handleDelete = async (r) => {
    const ok = await confirm({
      title: isRTL ? `حذف "${r.name}"؟` : `Delete "${r.name}"?`,
      destructive: true,
      confirmLabel: isRTL ? "نعم، احذف" : "Yes, delete",
    });
    if (ok) del.mutate(r.id);
  };

  return (
    <div className="space-y-6" data-testid="mycafe-drip">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-[#5C544F]">
          {isRTL ? "وصفات قهوة اليوم (Batch Brewer). يمكنك إضافة عدة دفعات تحت نفس الوصفة (HOT100 / HOT170 / COLD260 ...)." : "Coffee of the Day batch-brewer recipes. Add multiple batches under the same recipe (HOT100 / HOT170 / COLD260 ...)."}
        </p>
        {!editing && (
          <Button onClick={startNew} className="bg-[#2A2421] text-[#F9F6F0]" data-testid="drip-add-btn">
            <Plus className="w-4 h-4 me-1.5" />
            {isRTL ? "وصفة جديدة" : "New recipe"}
          </Button>
        )}
      </div>

      {editing && (
        <div className="soft-card rounded-2xl p-6 space-y-5" data-testid="drip-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>{isRTL ? "اسم الوصفة" : "Recipe name"} *</Label>
              <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder={isRTL ? "Ethiopia Yirgacheffe" : "Ethiopia Yirgacheffe"} className="mt-1.5 h-11" data-testid="drip-name-input" />
            </div>
            <div>
              <Label>{isRTL ? "الفرع (اختياري)" : "Branch (optional)"}</Label>
              <Input value={editing.branch_name || ""} onChange={(e) => setEditing({ ...editing, branch_name: e.target.value })} className="mt-1.5 h-11" data-testid="drip-branch-input" />
            </div>
          </div>

          <div className="space-y-4">
            {editing.batches.map((b, idx) => (
              <div key={idx} className="rounded-2xl border border-[#2A2421]/10 p-4" data-testid={`drip-batch-${idx}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs uppercase tracking-[0.2em] text-[#C86240]">
                    {isRTL ? `الدفعة ${idx + 1}` : `Batch ${idx + 1}`}
                  </div>
                  {editing.batches.length > 1 && (
                    <button onClick={() => removeBatch(idx)} className="p-1 hover:bg-red-50 rounded" data-testid={`drip-batch-del-${idx}`}>
                      <X className="w-3.5 h-3.5 text-red-500" />
                    </button>
                  )}
                </div>
                <div className="grid sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {BATCH_FIELDS.map(([key, label, ph]) => (
                    <div key={key}>
                      <Label className="text-xs">{label[isRTL ? "ar" : "en"]}</Label>
                      <Input
                        value={b[key] || ""}
                        onChange={(e) => setBatch(idx, { [key]: e.target.value })}
                        placeholder={ph[isRTL ? "ar" : "en"]}
                        className="mt-1 h-10"
                        data-testid={`drip-${idx}-${key}`}
                      />
                    </div>
                  ))}
                  <div className="sm:col-span-3 lg:col-span-4">
                    <Label className="text-xs">{isRTL ? "ملاحظات الدفعة" : "Batch notes"}</Label>
                    <Input
                      value={b.notes || ""}
                      onChange={(e) => setBatch(idx, { notes: e.target.value })}
                      className="mt-1 h-10"
                      data-testid={`drip-${idx}-notes`}
                    />
                  </div>
                </div>
              </div>
            ))}
            <Button variant="outline" onClick={addBatch} data-testid="drip-add-batch">
              <Plus className="w-4 h-4 me-1.5" />
              {isRTL ? "إضافة دفعة" : "Add batch"}
            </Button>
          </div>

          <div>
            <Label>{isRTL ? "ملاحظات عامة" : "Overall notes"}</Label>
            <Textarea rows={2} value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} className="mt-1.5" data-testid="drip-notes" />
          </div>

          <div className="flex gap-2">
            <Button onClick={() => save.mutate()} className="bg-[#C86240] text-white" data-testid="drip-save-btn">{isRTL ? "حفظ" : "Save"}</Button>
            <Button onClick={cancel} variant="outline"><X className="w-4 h-4 me-1.5" />{isRTL ? "إلغاء" : "Cancel"}</Button>
          </div>
        </div>
      )}

      {isLoading ? (
        <CardSkeleton rows={2} />
      ) : recipes.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#2A2421]/15 p-12 text-center bg-[#F9F6F0]/50">
          <div className="w-16 h-16 mx-auto rounded-full bg-[#3D6F86]/10 flex items-center justify-center mb-4">
            <Droplet className="w-7 h-7 text-[#3D6F86]" />
          </div>
          <div className="text-[#2A2421] font-medium text-lg">{isRTL ? "لا توجد وصفات قهوة اليوم بعد" : "No coffee-of-the-day recipes yet"}</div>
          <div className="text-sm text-[#5C544F] mt-1 max-w-md mx-auto">
            {isRTL ? "أنشئ وصفة قهوة اليوم مع دفعات متعددة (ساخن / بارد / Batch)." : "Create a coffee-of-the-day recipe with multiple batches (hot / cold / batch brew)."}
          </div>
        </div>
      ) : (
        <>
          {recipes.length > 2 && (
            <div className="relative max-w-sm">
              <Search className={`absolute ${isRTL ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#5C544F]`} />
              <Input value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder={isRTL ? "ابحث..." : "Search..."}
                className={`${isRTL ? "pr-9" : "pl-9"} h-10`}
                data-testid="drip-search"
              />
            </div>
          )}
          <div className="grid lg:grid-cols-2 gap-4">
            {filtered.map((r) => (
              <DripCard key={r.id} r={r} isRTL={isRTL}
                onEdit={() => startEdit(r)}
                onDuplicate={() => duplicate.mutate(r)}
                onDelete={() => handleDelete(r)} />
            ))}
          </div>
        </>
      )}

      {dialog}
    </div>
  );
}

function DripCard({ r, isRTL, onEdit, onDuplicate, onDelete }) {
  return (
    <div className="soft-card rounded-2xl p-5 hover:shadow-md transition-shadow" data-testid={`drip-card-${r.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className={`text-lg ${isRTL ? "font-serif-ar" : "font-serif-en"} text-[#2A2421] truncate`}>{r.name}</div>
          {r.branch_name && <div className="text-xs text-[#5C544F]">{r.branch_name}</div>}
        </div>
        <div className="flex gap-1 shrink-0">
          <button onClick={onEdit} className="p-1.5 hover:bg-[#2A2421]/5 rounded-full" data-testid={`drip-edit-${r.id}`} title={isRTL ? "تعديل" : "Edit"}>
            <Pencil className="w-3.5 h-3.5 text-[#5C544F]" />
          </button>
          <button onClick={onDuplicate} className="p-1.5 hover:bg-[#2A2421]/5 rounded-full" data-testid={`drip-dup-${r.id}`} title={isRTL ? "تكرار" : "Duplicate"}>
            <Copy className="w-3.5 h-3.5 text-[#5C544F]" />
          </button>
          <button onClick={onDelete} className="p-1.5 hover:bg-red-50 rounded-full" data-testid={`drip-del-${r.id}`} title={isRTL ? "حذف" : "Delete"}>
            <Trash2 className="w-3.5 h-3.5 text-red-500" />
          </button>
        </div>
      </div>
      <div className="mt-3 grid sm:grid-cols-2 gap-2">
        {(r.batches || []).map((b, i) => (
          <div key={i} className="rounded-lg bg-[#F9F6F0] p-2.5 text-xs text-[#2A2421]">
            <div className="font-semibold text-[#C86240] mb-1">{b.batch_name || `Batch ${i + 1}`}</div>
            <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
              {BATCH_FIELDS.slice(1).map(([k, lab]) => b[k] ? (
                <span key={k}><b>{lab[isRTL ? "ar" : "en"]}:</b> {b[k]}</span>
              ) : null)}
            </div>
          </div>
        ))}
      </div>
      {r.notes && <p className="mt-3 text-xs text-[#5C544F] italic">{r.notes}</p>}
    </div>
  );
}
